import React, { useCallback, useRef, useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { isNil, compose } from 'ramda'
import { Editor } from 'react-draft-wysiwyg'
import { EditorState, Modifier } from 'draft-js'
import TextCount from 'app/components/richTextEditor/TextCount'
import DoNotTranslate from 'app/components/textEditorDraft/customToolbar/DoNotTranslate'
import { convertFromHTML } from 'draft-convert'
import { stateToHTML } from 'draft-js-export-html'
import { defaultTextCount, textToTextCount } from 'libs/text'
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline'
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css'
import 'styles/input-notranslate.css'
import GenerateContentWithAIButton from 'app/components/textEditorDraft/customToolbar/GenerateContentWithAIButton'

const DONOTTRANSLATE_TAG = 'notranslate'
const TEXT_LIMIT_DEFAULT = 200

const styleMap = {
  DONOTTRANSLATE: {
    borderRadius: '2px',
    padding: '2px',
    background: 'rgb(211, 238, 240)',
  },
}

const stateToHTMLInlineStyleContentState = (editorState) => {
  const contentState = editorState.getCurrentContent()
  return stateToHTML(contentState, {
    blockStyleFn: () => ({
      style: {
        wordBreak: 'break-word',
      },
    }),
    inlineStyles: {
      DONOTTRANSLATE: {
        element: 'span',
        attributes: { class: DONOTTRANSLATE_TAG },
      },
    },
  })
}

const stateContentToTextCount = defaultTextCount(
  compose(textToTextCount, (stateContent) => stateContent.getPlainText()),
)

const createStateFromValue = (value) => {
  if (isNil(value)) return EditorState.createEmpty()

  const contentState = convertFromHTML({
    htmlToStyle: (tagName, element, currentStyle) => {
      return element.className === DONOTTRANSLATE_TAG
        ? currentStyle.add('DONOTTRANSLATE')
        : currentStyle
    },
  })(value)

  return EditorState.createWithContent(contentState)
}

const DoNotTranslateInput = ({
  value,
  onChange,
  isAIEnabled,
  aiGenerateTextUpdated,
  onGenerateContentWithAIClick,
  ...props
}) => {
  const firstState = createStateFromValue(value)
  const [editorState, setEditorState] = useState(firstState)
  const [textCount, setTextCount] = useState(
    stateContentToTextCount(editorState.getCurrentContent()),
  )
  const currentValueRef = useRef(
    stateToHTMLInlineStyleContentState(editorState),
  )

  const isError = !isNil(props.error) && props.touched

  const stripHtmlExceptClasses = (htmlString, classesToPreserve) => {
    const container = document.createElement('div')
    container.innerHTML = htmlString

    const processNode = (node) => {
      if (node.nodeType === Node.TEXT_NODE) return node.nodeValue
      if (node.nodeType === Node.ELEMENT_NODE) {
        const classList = Array.from(node.classList)
        const isPreserved = classesToPreserve.some((cls) =>
          classList.includes(cls),
        )
        return isPreserved
          ? node.outerHTML
          : Array.from(node.childNodes).map(processNode).join('')
      }
      return ''
    }

    return Array.from(container.childNodes).map(processNode).join('')
  }

  const onEditorStateChange = useCallback(
    (editorStateChange) => {
      const currentHtml = stateToHTMLInlineStyleContentState(editorStateChange)
      const newHtml = `<p style="word-break: break-word">${stripHtmlExceptClasses(
        currentHtml,
        [DONOTTRANSLATE_TAG],
      )}</p>`

      onChange(newHtml)
      setTextCount(
        stateContentToTextCount(editorStateChange.getCurrentContent()),
      )
      currentValueRef.current = newHtml
      setEditorState(editorStateChange)
    },
    [onChange],
  )

  const handlePastedText = (text, html, editorState) => {
    // Clean up pasted text by removing tags and newlines
    const cleanedText = text.replace(/<br\s*\/?>|[\r\n]+/g, ' ')

    const contentState = editorState.getCurrentContent()
    const selection = editorState.getSelection()

    // Insert the cleaned text into the editor
    const newContentState = Modifier.replaceText(
      contentState,
      selection,
      cleanedText,
    )

    // Push the updated content state into the editor
    const newEditorState = EditorState.push(
      editorState,
      newContentState,
      'insert-characters',
    )

    setEditorState(newEditorState)
    const rawContent = stateToHTMLInlineStyleContentState(newEditorState)
    onChange(rawContent)

    return true // We handled the paste event
  }

  const handleKeyCommand = (command) =>
    command === 'split-block' ? 'handled' : 'not-handled'

  const updateStateFromValue = () => {
    if (!isNil(value)) setEditorState(createStateFromValue(value))
  }

  useEffect(updateStateFromValue, [aiGenerateTextUpdated])

  const editorProps = {
    editorState,
    placeholder: props.required ? `${props.placeholder} *` : props.placeholder,
    toolbarClassName: 'toolbarClassName',
    wrapperClassName: isError
      ? 'wrapperClassNameError rounded-8'
      : 'wrapperClassNameNew rounded-8',
    editorClassName: 'editorClassName',
    customStyleMap: styleMap,
    handleKeyCommand,
    handlePastedText,
    onEditorStateChange,
    stripPastedStyles: true,
    blockStyleFn: () => 'cssClassToUseOnEveryHii',
    toolbar: {
      options: [],
      inline: { inDropdown: false },
      list: { inDropdown: false },
      textAlign: { inDropdown: false },
      link: { inDropdown: false },
      history: { inDropdown: false },
    },
    toolbarCustomButtons: [
      ...(isAIEnabled
        ? [
            <GenerateContentWithAIButton
              editorState={editorState}
              onGenerateContentWithAIClick={onGenerateContentWithAIClick}
            />,
          ]
        : []),
      <DoNotTranslate
        editorState={editorState}
        onUpdateEditorState={onEditorStateChange}
      />,
      <TextCount
        textCount={textCount}
        maxLength={props.maxLength || TEXT_LIMIT_DEFAULT}
      />,
    ],
  }

  return (
    <div className="rounded-8">
      <Editor {...editorProps} />
      {isError && (
        <div className="flex space-x-2 py-2">
          <div className="flex my-auto">
            <ErrorOutlineIcon className="text-red-500" fontSize="small" />
          </div>
          <label className="text-red text-14">{props.error}</label>
        </div>
      )}
    </div>
  )
}

DoNotTranslateInput.propTypes = {
  onChange: PropTypes.func,
  initialValue: PropTypes.string,
  maxLength: PropTypes.number,
  label: PropTypes.string,
}

export default React.memo(DoNotTranslateInput)
